import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import { RequireAuth } from './guards/RequireAuth';
import { RequireUnauthed } from './guards/RequireUnauthed';
import { TenantShell } from './shell/AppShell/index';

import AuthLayout from '../pages/auth/index';
import Login from '../pages/auth/login/index';
import Signup from '../pages/auth/signup/index';
import ForgotPassword from '../pages/auth/forgot-password/index';
import ResetPassword from '../pages/auth/reset-password/index';
import VerifyEmail from '../pages/auth/verify-email/index';
import Mfa from '../pages/auth/mfa/index';
import AuthCallback from '../pages/auth/callback/index';

import Onboarding from '../pages/onboarding/index';

import Dashboard from '../pages/dashboard/index';
import Inbox from '../pages/inbox/index';
import Compose from '../pages/inbox/compose/index';
import Contacts from '../pages/contacts/index';
import ContactPage from '../pages/contacts/ContactPage';
import Broadcasts from '../pages/broadcasts/index';
import BroadcastWizard from '../pages/broadcasts/new/index';
import BroadcastView from '../pages/broadcasts/view[id]/index';
import BroadcastDelivery from '../pages/broadcasts/delivery/index';
import Templates from '../pages/templates/index';
import TemplateView from '../pages/templates/view[id]/index';
import TemplateWizard from '../pages/templates/new/index';
import Flows from '../pages/flows/index';
import FlowsWorkspace from '../pages/flows/workspace/index';
import FlowsOverview from '../pages/flows/workspace/Overview';
import FlowView from '../pages/flows/view[id]/index';
import FlowBuilder from '../pages/flows/builder/index';
import FlowCalls from '../pages/flows/calls/index';
import Knowledge from '../pages/knowledge/index';
import AiTools from '../pages/ai/index';
// Chat insights (29 October 2026): AI reports on a period's chats.
import Insights from '../pages/insights/index';
import InsightReport from '../pages/insights/report/index';
// Suggest flows with AI (30 October 2026): a page of its own, outside the Flows workspace's sidebar.
import FlowIdeas from '../pages/flows/ideas/index';
// Bookings (7 October 2026): appointments, classes and call-outs.
import BookingsCalendar from '../pages/bookings/index';
import BookingsList from '../pages/bookings/list/index';
import BookingServices from '../pages/bookings/services/index';
import BookingStaff from '../pages/bookings/staff/index';
import BookingClasses from '../pages/bookings/classes/index';
import BookingSettings from '../pages/bookings/settings/index';
// Tickets (7 October 2026): queue, board, the ticket, tasks, reports and settings.
import TicketsQueue from '../pages/tickets/index';
import TicketsBoard from '../pages/tickets/board/index';
import TicketTasks from '../pages/tickets/tasks/index';
import TicketReports from '../pages/tickets/reports/index';
import TicketSettings from '../pages/tickets/settings/index';
import TicketDetail from '../pages/tickets/view/index';
import Numbers from '../pages/numbers/index';
import Channels from '../pages/channels/index';
import Voice from '../pages/voice/index';
import NumberBusinessProfile from '../pages/numbers/profile/index';

import Billing from '../pages/billing/index';
import Developers from '../pages/developers/index';
import BillingLedger from '../pages/billing/ledger/index';
import BillingTopup from '../pages/billing/topup/index';
import BillingUsage from '../pages/billing/usage/index';
import BillingAlerts from '../pages/billing/alerts/index';
// W47: refer and earn. Four pages rather than one, because a partner reading
// "what have I earned" and a partner asking for a payment are different jobs.
import Refer from '../pages/refer/index';
import ReferReferrals from '../pages/refer/referrals/index';
import ReferEarnings from '../pages/refer/earnings/index';
import ReferWithdrawals from '../pages/refer/withdrawals/index';

import SettingsLayout from '../pages/settings/_layout/index';
import Settings from '../pages/settings/index';
import SettingsProfile from '../pages/settings/profile/index';
import SettingsOrganisation from '../pages/settings/organisation/index';
import SettingsSecurity from '../pages/settings/security/index';
import SettingsTeam from '../pages/settings/team/index';
import SettingsNotifications from '../pages/settings/notifications/index';
import SettingsApiKeys from '../pages/settings/api-keys/index';
import SettingsWebhooks from '../pages/settings/webhooks/index';
import SettingsCannedReplies from '../pages/settings/canned-replies/index';
import SettingsContactFields from '../pages/settings/contact-fields/index';
import SettingsAssistant from '../pages/settings/assistant/index';
import SettingsAssistantBot from '../pages/settings/assistant/bot';
import SettingsAppearance from '../pages/settings/appearance/index';
import SettingsSms from '../pages/settings/sms/index';
import SettingsEmail from '../pages/settings/email/index';

import NotFound from '../pages/not-found/index';

export function AppRoutes() {
  return (
    <Routes>
      {/* Auth group: only for signed-out users. */}
      <Route
        path="/auth"
        element={
          <RequireUnauthed>
            <AuthLayout />
          </RequireUnauthed>
        }
      >
        <Route index element={<Navigate to="login" replace />} />
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<Signup />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="reset-password/:token" element={<ResetPassword />} />
        <Route path="mfa" element={<Mfa />} />
        {/* Where Google, Facebook and Apple redirect back to. Inside the
            signed-out group because the person is not signed in until the
            handoff code has been spent. */}
        <Route path="callback" element={<AuthCallback />} />
      </Route>

      {/* Email verification: reachable signed-in (soft-gate banner) or out. */}
      <Route path="/auth/verify-email" element={<AuthLayout />}>
        <Route index element={<VerifyEmail />} />
      </Route>

      {/* First-run onboarding: authed, but outside the app shell. */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <Onboarding />
          </RequireAuth>
        }
      />

      {/* Authenticated app shell. */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <TenantShell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<Inbox />} />
        {/* Message one person, or a few named people. A broadcast names its
            audience by tag or segment; this names people one at a time, and
            the message becomes a conversation in the inbox above. */}
        <Route path="inbox/compose" element={<Compose />} />
        <Route path="contacts" element={<Contacts />} />
        {/* A contact on a page of its own (8 October 2026): a row in the list opens
            the drawer, and "Open full page" in the drawer (or any link here)
            shows everything about the person at once. */}
        <Route path="contacts/:id" element={<ContactPage />} />
        {/* `/contacts/view/:id` is what the inbox's contact panel has always
            linked to. It matched no route at all, so clicking the contact's
            name in a conversation landed on Not found. */}
        <Route path="contacts/view/:id" element={<ContactRedirect />} />
        <Route path="broadcasts" element={<Broadcasts />} />
        {/* Creating a broadcast is a page with steps, not a drawer over the
            list. Ahead of `:id` for the same reason `/templates/new` is: the
            static segment must never be read as an id. */}
        <Route path="broadcasts/new" element={<BroadcastWizard />} />
        {/* Delivery across every broadcast, by day and channel (4 October 2026). */}
        <Route path="broadcasts/delivery" element={<BroadcastDelivery />} />
        <Route path="broadcasts/:id" element={<BroadcastView />} />
        <Route path="templates" element={<Templates />} />
        {/* Authoring is a page, not a drawer: four steps, a live preview beside
            the form, and a URL somebody can reload or come back to. A static
            segment outranks `:id`, so `/templates/new` never reads as an id. */}
        <Route path="templates/new" element={<TemplateWizard />} />
        <Route path="templates/:id/edit" element={<TemplateWizard />} />
        <Route path="templates/:id" element={<TemplateView />} />
        {/* The Flows workspace: the bot's flows in a sidebar on the right, the
            builder or the flow's detail in the content. The old list lives on
            at /flows/all for search, clone and delete. */}
        {/* Suggest flows with AI (30 October 2026): its own page, the AI on its right instead of the flows sidebar.
            Static segments, so never read as a flow id; under /flows, so owners and admins only. */}
        <Route path="flows/ideas" element={<FlowIdeas />} />
        <Route path="flows/ideas/:id" element={<FlowIdeas />} />
        <Route path="flows" element={<FlowsWorkspace />}>
          <Route index element={<FlowsOverview />} />
          <Route path="all" element={<Flows />} />
          {/* The calls flows made to other systems, for 48 hours (19 October 2026). Static, so never read as an id. */}
          <Route path="calls" element={<FlowCalls />} />
          <Route path=":id" element={<FlowView />} />
          <Route path=":id/builder" element={<FlowBuilder />} />
        </Route>
        <Route path="knowledge" element={<Knowledge />} />
        <Route path="ai" element={<AiTools />} />
        <Route path="insights" element={<Insights />} />
        <Route path="insights/:id" element={<InsightReport />} />
        <Route path="bookings" element={<BookingsCalendar />} />
        <Route path="bookings/list" element={<BookingsList />} />
        <Route path="bookings/services" element={<BookingServices />} />
        <Route path="bookings/staff" element={<BookingStaff />} />
        <Route path="bookings/classes" element={<BookingClasses />} />
        <Route path="bookings/settings" element={<BookingSettings />} />
        {/* A notice's link names one booking (`/bookings/{id}`): the list, with its drawer open. The static
            segments above outrank this, so `/bookings/list` is never read as an id. */}
        <Route path="bookings/:id" element={<BookingRedirect />} />
        <Route path="tickets" element={<TicketsQueue />} />
        <Route path="tickets/board" element={<TicketsBoard />} />
        <Route path="tickets/tasks" element={<TicketTasks />} />
        <Route path="tickets/reports" element={<TicketReports />} />
        <Route path="tickets/settings" element={<TicketSettings />} />
        {/* One ticket, the link in a ticket notice. The static segments above outrank it. */}
        <Route path="tickets/:id" element={<TicketDetail />} />
        <Route path="numbers" element={<Numbers />} />
        <Route path="channels" element={<Channels />} />
        <Route path="voice" element={<Voice />} />
        {/* The profile a recipient sees when they tap the number: picture,
            status line, description, address, websites, category. Per number,
            because each customer's number is their own business. */}
        <Route path="numbers/:id/profile" element={<NumberBusinessProfile />} />

        <Route path="developers" element={<Developers />} />

        <Route path="billing" element={<Billing />} />
        <Route path="billing/ledger" element={<BillingLedger />} />
        <Route path="billing/topup" element={<BillingTopup />} />
        <Route path="billing/usage" element={<BillingUsage />} />
        <Route path="billing/alerts" element={<BillingAlerts />} />
        <Route path="refer" element={<Refer />} />
        <Route path="refer/referrals" element={<ReferReferrals />} />
        <Route path="refer/earnings" element={<ReferEarnings />} />
        <Route path="refer/withdrawals" element={<ReferWithdrawals />} />

        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Settings />} />
          <Route path="profile" element={<SettingsProfile />} />
          <Route path="organisation" element={<SettingsOrganisation />} />
          <Route path="security" element={<SettingsSecurity />} />
          <Route path="team" element={<SettingsTeam />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          <Route path="api-keys" element={<SettingsApiKeys />} />
          <Route path="webhooks" element={<SettingsWebhooks />} />
          <Route path="canned-replies" element={<SettingsCannedReplies />} />
          <Route path="contact-fields" element={<SettingsContactFields />} />
          <Route path="assistant" element={<SettingsAssistant />} />
          <Route path="assistant/:id" element={<SettingsAssistantBot />} />
          <Route path="appearance" element={<SettingsAppearance />} />
          <Route path="sms" element={<SettingsSms />} />
          <Route path="email" element={<SettingsEmail />} />
        </Route>

      </Route>

      {/*
        404 lives OUTSIDE the authenticated group. Previously the only `path="*"`
        was a child of `/`, so an unknown URL for a signed-out visitor hit
        RequireAuth and silently redirected to login instead of showing NotFound
        (_UI_improvements/tenant/_app-shell.md §4 item 4).
      */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/**
 * The older `/contacts/view/:id` lands here (the inbox's contact panel) and opens the drawer over the list, as before.
 *
 * The contact page was replaced by a drawer over the list, and these are the
 * links that already exist: the inbox's contact panel, anything a tenant has
 * bookmarked, and the breadcrumbs of the old page. Redirecting is what keeps
 * them working, rather than a 404 for a contact that is right there.
 */
function ContactRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/contacts?contact=${id}` : '/contacts'} replace />;
}

/** `/bookings/{id}`, the link in a booking notice: the bookings list with that booking open. */
function BookingRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/bookings/list?booking=${encodeURIComponent(id)}` : '/bookings'} replace />;
}
